import { h } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import { linkify } from '../../../utils/a11y.utils';

import type { AddToCartRequest } from '../../../cart/models/cart.model';
import { useShopifyCurrency } from '../../../common/currency/shopify-currency.hook';
import { IconCaret } from '../../../common/icons/icon-caret';
import { buyItNow } from '../../../content/store/content.thunk';
import { useTranslation } from '../../../i18n';
import type { Product } from '../../../shopify/models/product.model';
import { useAppDispatch } from '../../../store/hooks';
import { toastService } from '../../../toast/toast.service';

export interface Props {
  key: string;
  product: Product;
  currencyCode?: string;
  locale?: string;
}

export const PerksProductCard = ({
  key,
  product,
  currencyCode = 'USD',
  locale = 'en-US',
}: Props) => {
  const { shopifyCurrency } = useShopifyCurrency();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [selectedVariantId, setSelectedVariantId] = useState(product?.variants?.[0]?.id || null);

  if (!product) return null;

  const hasMultipleVariants = product.variants.length > 1;

  const handleVariantChange = (e) => {
    setSelectedVariantId(Number(e.target.value));
  };

  const handleBuyNow = async () => {
    const productVariantId =
      product.variants.length === 1 ? product.variants[0].id : selectedVariantId;
    const result = await dispatch(buyItNow({ productVariantId, product } as AddToCartRequest));

    if (buyItNow.fulfilled.match(result)) {
      window.location.href = '/checkout';
    } else if (buyItNow.rejected.match(result)) {
      console.error('[Post > PostFooter] buyItNow failed:', result);
      toastService.error(t('common.error'), t('product.errorAddToCart'));
    }
  };

  const handleImageClick = () => {
    window.open(`/products/${product.handle}`, '_blank');
  };

  const availableVariants = useMemo(
    () => product.variants?.filter((v) => v.available) ?? [],
    [product.variants]
  );

  return (
    <div class="single-card">
      {product.featured_image && (
        <div class="single-card-image-wrapper" {...linkify(handleImageClick, product.title)}>
          <div
            class="single-card-image"
            style={{ backgroundImage: `url(${product.featured_image})` }}
          />
        </div>
      )}

      <div class="single-card-inner">
        <div class="single-card-title-price">
          <h4 class="single-collection-product-title card__heading h4 single-h4">
            {product.title}
          </h4>

          {/* Single Variant */}
          {!hasMultipleVariants && (
            <p class="single-collection-product-price" id={`single-product-price-${product.id}`}>
              {shopifyCurrency(product.variants[0].price)}
            </p>
          )}
        </div>

        <div class="single-card-buy-button">
          {/* Variant Selector */}
          {hasMultipleVariants && (
            <div class="select single-select-wrap single-variant-select">
              <select
                class="single-select select__select"
                aria-label={product.title}
                id={`single-variant-select-${product.id}`}
                value={selectedVariantId}
                onChange={handleVariantChange}
              >
                {product.variants.map(
                  (variant) =>
                    variant.available && (
                      <option key={variant.id} value={variant.id}>
                        {variant.title} - {shopifyCurrency(variant.price)}
                      </option>
                    )
                )}
              </select>
              <IconCaret />
            </div>
          )}

          <button class="button btn" disabled={!product.available} onClick={handleBuyNow}>
            {product.available ? t('product.buyNow') : t('product.soldOut')}
          </button>
        </div>
      </div>
    </div>
  );
};
